import { flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as R from 'fp-ts/Record';
import type { Element, TextNode } from 'parse5';
import type { ColorData, Decoration, ImageData, TextStyle } from 'ricos-schema';
import {
  Decoration_Type,
  Node_Type,
  PluginContainerData_Alignment,
  TextStyle_TextAlignment,
} from 'ricos-schema';

import { and } from '../../../../fp-utils';
import {
  createHeadingNode,
  createLink,
  createNode,
  createParagraphNode,
  createTextNode,
} from '../../../nodeUtils';
import type { Context, Rule } from './models';
import {
  getAttributes,
  getStyle,
  hasStyleFor,
  hasTag,
  isText,
  oneOf,
  toName,
} from './parse5-utils';

export const identityRule: Rule = [
  () => true,
  ({ visit }) =>
    (node: Element) =>
      visit(node),
];

export const textToText: Rule = [
  isText,
  context => (node: TextNode) => {
    const textNode = [createTextNode(node.value, context.decorations)];
    return node.parentNode.nodeName === 'li' ? [createParagraphNode(textNode)] : textNode;
  },
];

export const pToParagraph: Rule = [
  hasTag('p'),
  context => (el: Element) =>
    pipe(
      el,
      getStyle,
      O.chain(R.lookup('text-align')),
      O.map(toTextStyle),
      O.fold(
        () => [createParagraphNode(context.visit(el))],
        textStyle => [createParagraphNode(context.visit(el), { textStyle })]
      )
    ),
];

export const hToHeading: Rule = [
  flow(toName, /h[1-6]/.test.bind(/h[1-6]/)),
  context => (node: Element) =>
    [
      createHeadingNode(context.visit(node), {
        level: Number(node.nodeName.replace('h', '')),
      }),
    ],
];

export const aToLink: Rule = [
  and([hasTag('a'), flow(getAttributes, ({ href }) => !href?.startsWith('#'))]),
  context => (node: Element) => {
    const attrs = getAttributes(node);
    return context.addDecoration(
      Decoration_Type.LINK,
      { linkData: { link: createLink({ ...attrs, url: attrs.href }) } },
      node
    );
  },
];

export const aToAnchor: Rule = [
  and([hasTag('a'), flow(getAttributes, ({ href }) => !!href && href.startsWith('#'))]),
  context => (node: Element) => {
    const attrs = getAttributes(node);
    return context.addDecoration(
      Decoration_Type.ANCHOR,
      { anchorData: { ...attrs, anchor: attrs.href.substr(1) } },
      node
    );
  },
];

export const lToList: Rule = [
  oneOf(['ul', 'ol', 'li']),
  context => (node: Element) => {
    const nodeType = {
      ul: Node_Type.BULLETED_LIST,
      ol: Node_Type.ORDERED_LIST,
      li: Node_Type.LIST_ITEM,
    }[node.nodeName as Node_Type];
    return [
      createNode(nodeType, {
        nodes:
          nodeType === Node_Type.LIST_ITEM && node.childNodes.length === 0
            ? [createParagraphNode()]
            : context.visit(node),
        data: node.nodeName !== Node_Type.LIST_ITEM ? { indentation: 0 } : {},
      }),
    ];
  },
];

export const strongEmUToDecoration: Rule = [
  oneOf(['strong', 'em', 'u']),
  context => (node: Element) =>
    context.addDecoration(
      { strong: Decoration_Type.BOLD, em: Decoration_Type.ITALIC, u: Decoration_Type.UNDERLINE }[
        node.nodeName as Decoration_Type
      ],
      {},
      node
    ),
];

const toForegroundData = (color: string): Decoration['colorData'] => ({
  foreground: color,
});

const toBackgroundData = (color: string): Decoration['colorData'] => ({
  background: color,
});

const toTextAlignment = (textAlign: string): TextStyle['textAlignment'] => {
  switch (textAlign) {
    case 'left':
      return TextStyle_TextAlignment.LEFT;
    case 'right':
      return TextStyle_TextAlignment.RIGHT;
    case 'center':
      return TextStyle_TextAlignment.CENTER;
    case 'justify':
      return TextStyle_TextAlignment.JUSTIFY;
    default:
      return TextStyle_TextAlignment.AUTO;
  }
};

const toTextStyle = (style: string): TextStyle => {
  const textAlignment = toTextAlignment(style);
  return { textAlignment };
};

const toColorDecoration = (ctx: Context, el: Element) => (colorData: ColorData) =>
  ctx.addDecoration(Decoration_Type.COLOR, { colorData }, el);

export const colorStyleToTextColor: Rule = [
  and([hasTag('span'), hasStyleFor('color')]),
  context => (el: Element) =>
    pipe(
      el,
      getStyle,
      O.chain(R.lookup('color')),
      O.map(toForegroundData),
      O.fold(() => [], toColorDecoration(context, el))
    ),
];

// NOTE: Mention's HTML representation is not interactive. Might be fixed in the future
export const spanToMention: Rule = [
  and([hasTag('span'), flow(getAttributes, attrs => !!attrs['data-mention-name'])]),
  context => (node: Element) => {
    const {
      'data-mention-name': name,
      'data-mention-slug': slug,
      'data-mention-id': id,
    } = getAttributes(node);
    return context.addDecoration(
      Decoration_Type.MENTION,
      { mentionData: { name, slug, id } },
      node
    );
  },
];

// NOTE: Spoiler's HTML representation is not interactive. Might be fixed in the future
export const spanToSpoiler: Rule = [
  and([hasTag('span'), flow(getAttributes, attrs => !!attrs['data-spoiler'])]),
  context => (node: Element) => context.addDecoration(Decoration_Type.SPOILER, {}, node),
];

export const backgroundStyleToTextHighlight: Rule = [
  and([hasTag('span'), hasStyleFor('background-color')]),
  context => (el: Element) =>
    pipe(
      el,
      getStyle,
      O.chain(R.lookup('background-color')),
      O.map(toBackgroundData),
      O.fold(() => [], toColorDecoration(context, el))
    ),
];

const toImageData = (decorations: Decoration[], node: Element): ImageData => {
  const attrs = getAttributes(node);
  return {
    image: {
      src: { url: attrs.src },
      ...(attrs.width && { width: Number(attrs.width) }),
      ...(attrs.height && { height: Number(attrs.height) }),
    },
    ...(attrs.width
      ? {
          containerData: {
            alignment: PluginContainerData_Alignment.CENTER,
            width: { size: undefined, custom: attrs.width },
          },
        }
      : {}),
    altText: attrs.alt,
    link: decorations
      .filter(({ type }) => type === Decoration_Type.LINK)
      .map(({ linkData }) => linkData?.link)[0],
  };
};

export const imgToImage: Rule = [
  hasTag('img'),
  context => (node: Element) =>
    [createNode(Node_Type.IMAGE, { nodes: [], data: toImageData(context.decorations, node) })],
];
